import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { LogOut, DollarSign, ShoppingBag, Users, TrendingUp, Package, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fake statistics data
  const stats = [
    { label: 'Total Sales', value: '$45,250', icon: DollarSign, change: '+12.5%' },
    { label: 'Total Orders', value: '156', icon: ShoppingBag, change: '+8.2%' },
    { label: 'Total Customers', value: '89', icon: Users, change: '+5.7%' },
    { label: 'Average Order', value: '$290', icon: TrendingUp, change: '+3.2%' }
  ];

  // Fake sales data for the chart
  const salesData = [
    { month: 'Jan', sales: 12000 },
    { month: 'Feb', sales: 19000 },
    { month: 'Mar', sales: 15000 },
    { month: 'Apr', sales: 25000 },
    { month: 'May', sales: 22000 },
    { month: 'Jun', sales: 30000 }
  ];

  // Calculate max sales for scaling
  const maxSales = Math.max(...salesData.map(d => d.sales));
  const chartHeight = 200; // Height of the chart area

  // Generate path for the line
  const generatePath = (data: typeof salesData) => {
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 100 - (d.sales / maxSales) * 100;
      return `${x},${y}`;
    });
    return `M ${points.join(' L ')}`;
  };

  // Fake order distribution data
  const orderDistribution = [
    { category: 'Rings', percentage: 35 },
    { category: 'Necklaces', percentage: 25 },
    { category: 'Earrings', percentage: 20 },
    { category: 'Bracelets', percentage: 15 },
    { category: 'Other', percentage: 5 }
  ];

  // Fake recent orders
  const recentOrders = [
    {
      id: 'ORD-2024-001',
      customer: 'John Doe',
      date: '2024-03-15',
      total: '$1,250.00',
      status: 'Delivered',
      items: 2
    },
    {
      id: 'ORD-2024-002',
      customer: 'Jane Smith',
      date: '2024-03-14',
      total: '$2,800.00',
      status: 'Processing',
      items: 3
    },
    {
      id: 'ORD-2024-003',
      customer: 'Mike Johnson',
      date: '2024-03-14',
      total: '$950.00',
      status: 'Shipped',
      items: 1
    },
    {
      id: 'ORD-2024-004',
      customer: 'Sarah Wilson',
      date: '2024-03-13',
      total: '$3,200.00',
      status: 'Processing',
      items: 2
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'Processing':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'Shipped':
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-24">
      <div className="container">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-gold/10 to-gold/5 p-8 flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-serif text-gray-900">Welcome back, {user?.name}</h1>
                <p className="text-gray-600">Here's what's happening with your store today.</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{stat.label}</p>
                      <p className="text-2xl font-serif text-gray-900 mt-1">{stat.value}</p>
                    </div>
                    <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center">
                      <stat.icon className="w-6 h-6 text-gold" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="text-sm text-green-600">{stat.change}</span>
                    <span className="text-sm text-gray-600"> from last month</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-8">
              {/* Sales Chart */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-lg font-serif text-gray-900 mb-4">Sales Overview</h2>
                <div className="relative h-64">
                  {/* Y-axis labels */}
                  <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-gray-500">
                    <span>${maxSales.toLocaleString()}</span>
                    <span>${(maxSales * 0.75).toLocaleString()}</span>
                    <span>${(maxSales * 0.5).toLocaleString()}</span>
                    <span>${(maxSales * 0.25).toLocaleString()}</span>
                    <span>$0</span>
                  </div>

                  {/* Chart area */}
                  <div className="absolute left-12 right-0 top-0 bottom-8">
                    {/* Grid lines */}
                    <div className="absolute inset-0 flex flex-col justify-between">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="w-full border-t border-gray-100" />
                      ))}
                    </div>

                    {/* Animated line chart */}
                    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                      {/* Gradient for the area under the line */}
                      <defs>
                        <linearGradient id="salesGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="rgba(212, 175, 55, 0.2)" />
                          <stop offset="100%" stopColor="rgba(212, 175, 55, 0)" />
                        </linearGradient>
                      </defs>

                      {/* Area under the line */}
                      <motion.path
                        d={`${generatePath(salesData)} L 100,100 L 0,100 Z`}
                        fill="url(#salesGradient)"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.5 }}
                      />

                      {/* Animated line */}
                      <motion.path
                        d={generatePath(salesData)}
                        fill="none"
                        stroke="rgba(212, 175, 55, 0.8)"
                        strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                      />

                      {/* Data points */}
                      {salesData.map((data, i) => (
                        <motion.circle
                          key={data.month}
                          cx={`${(i / (salesData.length - 1)) * 100}`}
                          cy={`${100 - (data.sales / maxSales) * 100}`}
                          r="3"
                          fill="rgba(212, 175, 55, 1)"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 1 + i * 0.1 }}
                        />
                      ))}
                    </svg>
                  </div>

                  {/* X-axis labels */}
                  <div className="absolute left-12 right-0 bottom-0 h-8 flex justify-between text-xs text-gray-500">
                    {salesData.map(data => (
                      <span key={data.month}>{data.month}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Distribution */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-lg font-serif text-gray-900 mb-4">Order Distribution</h2>
                <div className="space-y-4">
                  {orderDistribution.map((item) => (
                    <div key={item.category}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{item.category}</span>
                        <span>{item.percentage}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gold/20 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${item.percentage}%` }}
                          transition={{ duration: 1, delay: 0.5 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="p-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-lg font-serif text-gray-900 mb-4">Recent Orders</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-600">
                        <th className="pb-4">Order ID</th>
                        <th className="pb-4">Customer</th>
                        <th className="pb-4">Date</th>
                        <th className="pb-4">Items</th>
                        <th className="pb-4">Total</th>
                        <th className="pb-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.id} className="border-t border-gray-100">
                          <td className="py-4 text-sm">{order.id}</td>
                          <td className="py-4 text-sm">{order.customer}</td>
                          <td className="py-4 text-sm">{order.date}</td>
                          <td className="py-4 text-sm">{order.items} items</td>
                          <td className="py-4 text-sm">{order.total}</td>
                          <td className="py-4">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(order.status)}
                              <span className="text-sm">{order.status}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 